// frontend/src/services/contentService.js
import api from './api';
import axios from 'axios';

/* =========================================================================
   Derivar /api/ raíz desde la baseURL actual de `api`
   Si `api.defaults.baseURL` = https://agencia.geves.cl/api/content/
   entonces API_ROOT = https://agencia.geves.cl/api
   ========================================================================= */
const BASE = (api?.defaults?.baseURL || '').replace(/\/+$/, ''); // sin slash final
// quita sólo el sufijo /content (si existe)
const API_ROOT = BASE.replace(/\/(?:api\/)?content\/?$/i, '');

// Segundo cliente apuntando a /api (sin /content)
const apiRoot = axios.create({
  baseURL: `${API_ROOT}/`,          // ej: https://agencia.geves.cl/api/
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Hereda los mismos interceptores que `api`: CSRF + JWT
function getCsrfCookie() {
  const name = 'csrftoken';
  const match = document.cookie.split(';').find(c => c.trim().startsWith(name + '='));
  return match ? decodeURIComponent(match.trim().substring(name.length + 1)) : null;
}
apiRoot.interceptors.request.use(config => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers['X-CSRFToken'] = getCsrfCookie();
  }
  const token = localStorage.getItem('access_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

// --------- FUNCIÓN ÚNICA PARA IG BUSINESS DISCOVERY ----------
const getIGBusinessDiscovery = (params) => {
  // sanea username
  const username = (params?.username || '').trim().replace(/^@+/, '');
  const outParams = {
    ...params,
    username,
  };
  return apiRoot.get('instagram/api/business-discovery/', { params: outParams });
};

const contentService = {
  // ============================================
  // CUENTAS
  // ============================================
  getCuentas: () => api.get('/cuentas/obtener/'),
  cambiarCuenta: (cuentaId) => api.post('/cuentas/cambiar/', { cuenta_id: cuentaId }),
  getCuentaActual: () => api.get('/cuentas/actual/'),

  // ============================================
  // POSTS
  // ============================================
  getPosts: (params) => api.get('/posts/', { params }),
  getPost: (id) => api.get(`/posts/${id}/`),
  // Endpoint especial para enlaces de email (cambia cuenta automáticamente si es necesario)
  getPostFromEmailLink: (id) => api.get(`/posts/email-link/${id}/`),
  getPublishedPosts: (params = {}) => api.get('/posts/published/', { params }),
  importAnalytics: (formData) => api.post('/posts/import-analytics/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  setPublicationUrl: (postId, url) => api.patch(`/posts/${postId}/set-url/`, { url_publicacion: url }),
  getScrapedCandidates: (postId) => api.get(`/posts/${postId}/scraped-candidates/`),
  linkScrapedPost: (postId, scrapedPostId) => api.post(`/posts/${postId}/link-scraped/`, { scraped_post_id: scrapedPostId }),
  unlinkScrapedPost: (postId) => api.post(`/posts/${postId}/link-scraped/`, { scraped_post_id: null }),
  createPost: (data) => api.post('/posts/', data),
  updatePost: (id, data) => api.patch(`/posts/${id}/`, data),
  deletePost: (id) => api.delete(`/posts/${id}/`),

  updatePostInfo: (postId, infoData) => api.patch(`/posts/${postId}/update_info/`, infoData),

  getCalendar: (startDate, endDate, extraParams = {}) =>
    api.get('/posts/calendar/', { params: { start_date: startDate, end_date: endDate, ...extraParams } }),

  assignEditor: (postId, editorId) => api.post(`/posts/${postId}/assign_editor/`, { editor_id: editorId }),
  submitCreative: (postId, data) => api.post(`/posts/${postId}/submit_creative/`, data),
  approveCreativeIdea: (postId, action, notes) => api.post(`/posts/${postId}/approve_creative_idea/`, { action, notes }),
  approveCopy: (postId, action, notes) => api.post(`/posts/${postId}/approve_copy/`, { action, notes }),
  readyForFinalReview: (postId) => api.post(`/posts/${postId}/ready_for_final_review/`),
  finalApprove: (postId, action, notes) => api.post(`/posts/${postId}/final_approve/`, { action, notes }),
  clientApprove: (postId, action = 'approved', notes = '') => api.post(`/posts/${postId}/client_approve/`, { action, notes }),

  getPreview: (postId) => api.get(`/posts/${postId}/preview/`),
  schedulePost: (postId, publicationDate) => api.post(`/posts/${postId}/schedule/`, publicationDate ? { publication_date: publicationDate } : {}),
  publishPost: (postId) => api.post(`/posts/${postId}/publish/`), // Endpoint antiguo (solo Instagram con lógica compleja)
  publishNow: (postId) => api.post(`/posts/${postId}/publish-now/`), // Nuevo endpoint (Instagram y TikTok con Celery)
  publishManual: (postId, url_publicacion) => api.post(`/posts/${postId}/publish-manual/`, { url_publicacion }), // Publicación manual con URL

  // ============================================
  // MEDIA
  // ============================================
  uploadMedia: (postId, formData) =>
    api.post('/media/upload/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Presigned URL: pide URL firmada para subir directo a Spaces
  getPresignedUploadUrl: (postId, fileName, fileType) =>
    api.post('/media/presigned_upload/', { post_id: postId, file_name: fileName, file_type: fileType }),

  // Confirma el upload directo y registra el MediaFile en la base de datos
  confirmUpload: (postId, publicUrl, fileType, fileSize, order, skipNotify = true) =>
    api.post('/media/confirm_upload/', {
      post_id: postId,
      public_url: publicUrl,
      file_type: fileType,
      file_size: fileSize,
      order,
      skip_notify: skipNotify,
    }),

  // Upload directo a Spaces con presigned URL, con soporte de progreso
  uploadToPresignedUrl: (presignedUrl, file, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.setRequestHeader('x-amz-acl', 'public-read');
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(file);
    });
  },

  // ============================================
  // INFO ATTACHMENTS
  // ============================================
  uploadInfoAttachment: (postId, file) => {
    const formData = new FormData();
    formData.append('post_id', postId);
    formData.append('file', file);
    return api.post('/info-attachments/upload/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteInfoAttachment: (attachmentId) => api.delete(`/info-attachments/${attachmentId}/`),
  notifyMediaBatch: (postId, mediaIds) =>
    api.post('/media/notify_batch/', { post_id: postId, media_ids: mediaIds }),
  approveMedia: (mediaId) => api.post(`/media/${mediaId}/approve/`),
  unapproveMedia: (mediaId) => api.post(`/media/${mediaId}/unapprove/`),
  deleteMedia: (mediaId) => api.delete(`/media/${mediaId}/`),
  downloadMedia: (mediaId) => {
    api.get(`/media/${mediaId}/download/`, { responseType: 'blob' })
      .then(res => {
        const disposition = res.headers['content-disposition'] || '';
        const match = disposition.match(/filename="?(.+?)"?$/);
        const filename = match ? match[1] : 'archivo';
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      });
  },

  // ============================================
  // COMMENTS
  // ============================================
  getComments: (postId, filters = {}) => api.get('/comments/', { params: { post_id: postId, ...filters } }),
  createComment: (data, file = null) => {
    if (file) {
      const formData = new FormData();
      Object.entries(data).forEach(([k, v]) => v !== null && v !== undefined && formData.append(k, v));
      formData.append('attachment', file);
      formData.append('attachment_name', file.name);
      return api.post('/comments/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post('/comments/', data);
  },
  updateComment: (commentId, data, file = null) => {
    if (file) {
      const formData = new FormData();
      Object.entries(data).forEach(([k, v]) => v !== null && v !== undefined && formData.append(k, v));
      formData.append('attachment', file);
      formData.append('attachment_name', file.name);
      return api.patch(`/comments/${commentId}/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.patch(`/comments/${commentId}/`, data);
  },
  deleteComment: (commentId) => api.delete(`/comments/${commentId}/`),
  resolveComment: (commentId) => api.post(`/comments/${commentId}/resolve/`),
  unresolveComment: (commentId) => api.post(`/comments/${commentId}/unresolve/`),

  // ============================================
  // PLATFORMS
  // ============================================
  getPlatforms: () => api.get('/platforms/'),
  getPlatform: (id) => api.get(`/platforms/${id}/`),

  // ============================================
  // INFLUENCERS
  // ============================================
  getInfluencers: () => api.get('/influencers/?tipo=cuenta'),
  getInfluencer: (id) => api.get(`/influencers/${id}/`),
  createInfluencer: (data) => api.post('/influencers/', { ...data, tipo: 'cuenta' }),
  updateInfluencer: (id, data) => api.patch(`/influencers/${id}/`, data),
  deleteInfluencer: (id) => api.delete(`/influencers/${id}/`),
  generateInstagramLink: (id) => api.post(`/influencers/${id}/generate-instagram-link/`),
  generateInstagramDirectLink: (id) => api.post(`/influencers/${id}/generate-instagram-direct-link/`),
  generateTikTokLink: (id) => api.post(`/influencers/${id}/generate-tiktok-link/`),
  saveInfluencerProfileImage: (id) => api.post(`/influencers/${id}/save-profile-image/`),

  // COMPETENCIA
  // ============================================
  getCompetidores: () => api.get('/influencers/?tipo=competencia'),

  // Snapshots de seguidores
  getFollowerSnapshotToday: (influencerId) =>
    api.get(`/influencers/${influencerId}/follower-snapshot/`),
  saveFollowerSnapshot: (influencerId, data) =>
    api.post(`/influencers/${influencerId}/follower-snapshot/`, data),
  getCompetidor: (id) => api.get(`/influencers/${id}/`),
  createCompetidor: (data) => api.post('/influencers/', { ...data, tipo: 'competencia' }),
  updateCompetidor: (id, data) => api.patch(`/influencers/${id}/`, data),
  deleteCompetidor: (id) => api.delete(`/influencers/${id}/`),
  // TikTok público vía Apify (no requiere OAuth del competidor)
  getTikTokCompetitorPosts: (username, limit = 20) =>
    apiRoot.get('api/tiktok/competitor-posts/', { params: { username, limit }, timeout: 130000 }),

  // EnsembleData: TikTok público (más rápido que Apify, ~30s)
  getEnsembleDataTikTokPosts: (username, limit = 20) =>
    apiRoot.get('api/tiktok/ensembledata/tiktok-posts/', { params: { username, limit }, timeout: 45000 }),

  // EnsembleData: Instagram público (alternativa cuando Business Discovery no funciona)
  getEnsembleDataIGPosts: (username, limit = 20) =>
    apiRoot.get('api/tiktok/ensembledata/instagram-posts/', { params: { username, limit }, timeout: 45000 }),

  // SociaVault: perfil TikTok público (seguidores, likes totales, video count)
  getSociaVaultTikTokProfile: (handle) =>
    apiRoot.get('api/tiktok/sociavault/tiktok-profile/', { params: { handle }, timeout: 20000 }),

  // SociaVault: Facebook posts públicos de páginas de la competencia (fallback, requiere créditos)
  getSociaVaultFacebookPosts: (username, limit = 20) =>
    apiRoot.get('api/tiktok/sociavault/facebook-posts/', { params: { username, limit }, timeout: 60000 }),

  // Apify: Facebook posts públicos (reemplaza SociaVault para Facebook)
  getApifyFacebookPosts: (username, limit = 20) =>
    apiRoot.get('api/tiktok/apify/facebook-posts/', { params: { username, limit }, timeout: 120000 }),

  // SociaVault: perfil Facebook público (seguidores, likes totales) — fallback, requiere créditos
  getSociaVaultFacebookProfile: (username) =>
    apiRoot.get('api/tiktok/sociavault/facebook-profile/', { params: { username }, timeout: 20000 }),

  // Meta Graph API: perfil de página Facebook (seguidores) — gratis, usa IG_SYSTEM_USER_TOKEN
  getMetaFacebookProfile: (username) =>
    apiRoot.get('api/tiktok/meta/facebook-profile/', { params: { username }, timeout: 15000 }),

  // ============================================
  // USERS
  // ============================================
  getUsers: () => api.get('/users/'),

  // ============================================
  // API KEYS EXTERNAS
  // ============================================
  getApiKeys: () => api.get('/api-keys/'),
  createApiKey: (data) => api.post('/api-keys/', data),
  toggleApiKey: (id, is_active) => api.patch(`/api-keys/${id}/`, { is_active }),
  deleteApiKey: (id) => api.delete(`/api-keys/${id}/`),

  // ============================================
  // DESARROLLA
  // ============================================
  getDesarrolla: () => api.get('/desarrolla/'),
  getDesarrollaById: (id) => api.get(`/desarrolla/${id}/`),
  createDesarrolla: (data) => api.post('/desarrolla/', data),
  updateDesarrolla: (id, data) => api.patch(`/desarrolla/${id}/`, data),
  deleteDesarrolla: (id) => api.delete(`/desarrolla/${id}/`),
  uploadDesarrollaProfileImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/desarrolla/upload_profile_image/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  },

  // ============================================
  // CLIENTES
  // ============================================
  getClientes: () => api.get('/clientes/'),
  getClienteById: (id) => api.get(`/clientes/${id}/`),
  createCliente: (data) => api.post('/clientes/', data),
  updateCliente: (id, data) => api.patch(`/clientes/${id}/`, data),
  deleteCliente: (id) => api.delete(`/clientes/${id}/`),

  // ============================================
  // ONBOARDING / INVITACIONES
  // ============================================
  verificarEstadoOnboarding: () => api.get('/onboarding/verificar/'),
  crearCuentaOnboarding: (data) => api.post('/onboarding/crear-cuenta/', data),
  unirseConInvitacion: (data) => api.post('/onboarding/unirse/', data),

  listarInvitaciones: () => api.get('/invitaciones/'),
  crearInvitacion: (data) => api.post('/invitaciones/crear/', data),
  reenviarInvitacion: (invitacionId) => api.post(`/invitaciones/${invitacionId}/reenviar/`),
  cancelarInvitacion: (invitacionId) => api.post(`/invitaciones/${invitacionId}/cancelar/`),
  getTiposUsuario: () => api.get('/tipos-usuario/'),

  // ============================================
  // INSTAGRAM (Business Discovery)
  // ============================================
  getIGBusinessDiscovery,
  getDiscoveryApify:        (username) => apiRoot.post('instagram/api/discovery/apify/', { username }),
  getDiscoverySaved:        ()       => apiRoot.get('instagram/api/discovery/saved/'),
  saveDiscoveryPerfil:      (data)   => apiRoot.post('instagram/api/discovery/saved/', data),
  deleteDiscoveryPerfil:    (id)     => apiRoot.delete(`instagram/api/discovery/saved/${id}/`),
  patchDiscoveryNotas:      (id, notas) => apiRoot.patch(`instagram/api/discovery/saved/${id}/`, { notas }),
  getTrackedProfilesStats:  ()         => apiRoot.get('api/tracked-profiles/stats/'),
  getTrackedProfiles:       (params)   => apiRoot.get('api/tracked-profiles/', { params }),
  createTrackedProfile:     (username) => apiRoot.post('api/tracked-profiles/', { username }),
  getMercadoRanking:        (days = 30) => apiRoot.get('api/mercado/ranking/', { params: { days } }),
  searchInfluencers:        (q)        => apiRoot.get('api/influencers/search/', { params: { q } }),
  queueInfluencer:          (username) => apiRoot.post('api/influencers/search/', { username }),
  rescrapeInfluencer:       (username, maxPosts = 30) => apiRoot.post('api/influencers/rescrape/', { username, max_posts: maxPosts }),

  // ── Mercado ──────────────────────────────────────────────────────────
  getMercado:              ()           => apiRoot.get('api/mercado/'),
  setMercadoMarca:         (profile_id) => apiRoot.post('api/mercado/marca/', { profile_id }),
  removeMercadoMarca:      ()           => apiRoot.delete('api/mercado/marca/'),
  addMercadoRole:          (role, profile_id) => apiRoot.post(`api/mercado/${role}/`, { profile_id }),
  removeMercadoRole:       (role, profile_id) => apiRoot.delete(`api/mercado/${role}/${profile_id}/`),
  getMercadoProfileStats:  (profile_id) => apiRoot.get(`api/mercado/profile/${profile_id}/stats/`),
  setProfileMaxPosts:      (profile_id, max_posts) => apiRoot.patch(`api/mercado/profile/${profile_id}/max-posts/`, { max_posts }),
  getMercadoCompare:       (competitor_id, marca_id, force = false) => apiRoot.post('api/mercado/compare/', { competitor_id, marca_id, force }),
  getMarcaPosts:           (params = {}) => apiRoot.get('api/mercado/marca/posts/', { params }),
  getPublicadosPosts:      (params = {}) => apiRoot.get('api/mercado/marca/posts/', { params }),
  getMarcaSentiment:       (profile_id, force = false) => apiRoot.post('api/mercado/marca/sentiment/', { profile_id, force }),
  setProfileTikTok:        (profile_id, tiktok_username) => apiRoot.patch(`api/mercado/profile/${profile_id}/tiktok/`, { tiktok_username }),
  getMercadoTikTokPosts:       (params = {}) => apiRoot.get('api/mercado/tiktok-posts/', { params }),
  getMercadoFollowersHistory: (params = {}) => apiRoot.get('api/mercado/followers-history/', { params }),

  // ============================================
  // ENDPOINTS PÚBLICOS (sin autenticación) — /reporte-geves-vista
  // ============================================
  getPublicMercado:              ()           => apiRoot.get('api/public/reporte/mercado/'),
  getPublicMarcaPosts:           (params = {}) => apiRoot.get('api/public/reporte/posts/', { params }),
  getPublicMercadoTikTokPosts:   (params = {}) => apiRoot.get('api/public/reporte/tiktok-posts/', { params }),
  getPublicMercadoFollowersHistory: (params = {}) => apiRoot.get('api/public/reporte/followers-history/', { params }),

  // ============================================
  // META OAUTH (conectar cuenta propia vía Facebook Login)
  // ============================================
  // Obtener URL de autorización → redirigir el usuario a ella
  getMetaOAuthUrl: () =>
    apiRoot.get('instagram/meta/connect/'),
  // Instagram Business Login (OAuth directo con Instagram)
  getIgBusinessOAuthUrl: () =>
    apiRoot.get('instagram/meta/ig-connect/'),

  // ============================================
  // MARKETING API (Ads Insights) — adicional, no reemplaza lo anterior
  // ============================================
  // URL de reconexión que agrega el permiso ads_read
  getMetaAdsOAuthUrl: () =>
    apiRoot.get('instagram/meta/ads-connect/'),
  // Lista de cuentas publicitarias (act_...) accesibles con el token actual
  getMetaAdAccounts: () =>
    apiRoot.get('instagram/meta/ad-accounts/'),
  // Ads Insights a nivel de anuncio, cruzados con el post orgánico
  getMetaAdsInsights: (adAccountId, since, until) =>
    apiRoot.get('instagram/meta/ads-insights/', {
      params: { ad_account_id: adAccountId, since, until },
    }),
  // Carga masiva de posts desde Excel
  bulkImportPosts: (formData) =>
    api.post('posts/bulk-import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Estado de la conexión actual + cuentas IG sincronizadas
  getInstagramMetaEstado: () =>
    apiRoot.get('instagram/meta/estado/'),
  // Re-sincronizar cuentas desde Meta
  sincronizarInstagramMeta: () =>
    apiRoot.post('instagram/meta/sincronizar/'),
  // Desconectar la cuenta
  desconectarInstagramMeta: () =>
    apiRoot.post('instagram/meta/desconectar/'),
  // Perfil extendido IG + FB desde la Graph API
  getMetaPerfil: () =>
    apiRoot.get('instagram/meta/perfil/'),

  // ============================================
  // TIKTOK OAUTH (conectar cuenta propia vía TikTok for Developers)
  // ============================================
  // Obtener URL de autorización → redirigir/abrir popup con ella
  getTikTokOAuthUrl: () =>
    apiRoot.get('api/tiktok/oauth-config/'),
  // Cuenta(s) TikTok conectada(s) del usuario autenticado
  getMyTikTokAccount: () =>
    apiRoot.get('api/tiktok/tiktok-account/me/'),
  // Re-sincronizar datos desde la API de TikTok
  refreshTikTokAccount: () =>
    apiRoot.post('api/tiktok/tiktok-account/refresh/'),
  // Marcar una cuenta como principal
  setPrimaryTikTokAccount: (accountId) =>
    apiRoot.post(`api/tiktok/tiktok-account/${accountId}/set-primary/`),
  // Desconectar una cuenta
  disconnectTikTokAccount: (accountId) =>
    apiRoot.delete(`api/tiktok/tiktok-account/${accountId}/disconnect/`),

  // ============================================
  // TIKTOK ANALYTICS (datos trackeados cada 10 min de las cuentas conectadas)
  // ============================================
  // Cuentas trackeadas (incluye las vinculadas a una cuenta OAuth propia)
  getTrackedTikTokAccounts: () =>
    apiRoot.get('api/tiktok/tracked-accounts/'),
  // Dispara una actualización inmediata desde la API de TikTok
  fetchTikTokAccountData: (trackedAccountId) =>
    apiRoot.post(`api/tiktok/tracked-accounts/${trackedAccountId}/fetch_data/`),
  // Videos con métricas (último snapshot por video)
  getTikTokAccountPosts: (trackedAccountId, days = 30) =>
    apiRoot.get('api/tiktok/tracked-accounts/posts/', { params: { account_id: trackedAccountId, days } }),
  // Serie histórica agregada (para tendencias)
  getTikTokAccountAnalytics: (trackedAccountId, days = 30) =>
    apiRoot.get('api/tiktok/tracked-accounts/analytics/', { params: { account_id: trackedAccountId, days } }),
  // Historial diario de seguidores (para gráfico de evolución)
  getTikTokFollowersHistory: (trackedAccountId, days = 90) =>
    apiRoot.get('api/tiktok/tracked-accounts/followers_history/', { params: { account_id: trackedAccountId, days } }),

  getIgAccountInsights: ({ since, until } = {}) => {
    const params = {};
    if (since) params.since = since;
    if (until) params.until = until;
    return apiRoot.get('instagram/meta/account-insights/', { params });
  },
  // Posts con métricas completas (reach, impressions, saved) de cuenta propia
  getInstagramMetaPosts: (igUserId, { since, until, limit = 100 } = {}) =>
    apiRoot.get('instagram/meta/posts/', { params: { ig_user_id: igUserId, limit, since, until }, timeout: 30000 }),

  // ============================================
  // FACEBOOK PAGE (cuenta propia conectada vía Meta OAuth)
  // ============================================
  // Estado de la conexión: páginas FB sincronizadas
  getFacebookMetaEstado: () =>
    apiRoot.get('instagram/meta/facebook/estado/'),
  // Re-sincronizar páginas desde Meta
  sincronizarFacebookMeta: () =>
    apiRoot.post('instagram/meta/facebook/sincronizar/'),
  // Posts recientes de la página FB con reacciones, comentarios y shares
  getFacebookMetaPosts: (pageId, { since, until, limit = 100 } = {}) =>
    apiRoot.get('instagram/meta/facebook/posts/', { params: { page_id: pageId, limit, since, until }, timeout: 30000 }),
  // Insights de la página FB (impresiones, alcance, usuarios comprometidos)
  getFacebookMetaInsights: (pageId, period = 'day') =>
    apiRoot.get('instagram/meta/facebook/insights/', { params: { page_id: pageId, period }, timeout: 20000 }),

  // Full data: últimos 30 posts + todas las métricas + demographics
  getIgFullData: (igUserId = '', limit = 30) =>
    apiRoot.get('instagram/meta/ig-full/', { params: { ig_user_id: igUserId, limit }, timeout: 60000 }),
  // Datos desde BD (job diario) — sin llamar a la API de Instagram
  getIgDbData: (limit = 50, dateFrom, dateTo, igUserId = '') =>
    apiRoot.get('instagram/meta/ig-db/', { params: { limit, date_from: dateFrom, date_to: dateTo, ig_user_id: igUserId } }),
  getIgAccounts: () =>
    apiRoot.get('instagram/meta/ig-accounts/'),
  grabarIgSnapshot: (igUserId, payload) =>
    apiRoot.post('instagram/meta/ig-grabar/', { ig_user_id: igUserId, ...payload }),
  getFbDbData: (dateFrom, dateTo, pageId = '') =>
    apiRoot.get('instagram/meta/fb-db/', { params: { date_from: dateFrom, date_to: dateTo, page_id: pageId } }),
  getFbPages: () =>
    apiRoot.get('instagram/meta/fb-pages/'),
  grabarFbSnapshot: (pageId, payload) =>
    apiRoot.post('instagram/meta/fb-grabar/', { page_id: pageId, ...payload }),
  getFbFullData: (pageId = '', limit = 30) =>
    apiRoot.get('instagram/meta/fb-full/', { params: { page_id: pageId, limit }, timeout: 60000 }),

  // ============================================
  // SOCIALINSIDER
  // ============================================
  // Configuración (API key)
  getSIConfig: () =>
    apiRoot.get('instagram/socialinsider/config/'),
  setSIConfig: (apiKey) =>
    apiRoot.post('instagram/socialinsider/config/', { api_key: apiKey }),
  deleteSIConfig: () =>
    apiRoot.delete('instagram/socialinsider/config/delete/'),
  // Proyectos
  getSIProyectos: () =>
    apiRoot.get('instagram/socialinsider/proyectos/', { timeout: 20000 }),
  // Perfiles de un proyecto
  getSIPerfiles: (proyectoSlug) =>
    apiRoot.get('instagram/socialinsider/perfiles/', { params: { proyecto_slug: proyectoSlug }, timeout: 20000 }),
  // Posts de un perfil
  getSIPosts: (params) =>
    apiRoot.get('instagram/socialinsider/posts/', { params, timeout: 30000 }),
  // Métricas de un perfil
  getSIMetricas: (params) =>
    apiRoot.get('instagram/socialinsider/metricas/', { params, timeout: 30000 }),

  // ============================================
  // REPORTES SOCIALES (persistencia /reporte)
  // ============================================
  getIgPostCaption: (url) =>
    apiRoot.get('instagram/ig-caption/', { params: { url } }),
  getReportesSociales: () =>
    apiRoot.get('instagram/reportes/'),
  saveReporteSocial: (data) =>
    apiRoot.post('instagram/reportes/save/', data),
  deleteReporteSocial: (plataforma) =>
    apiRoot.delete(`instagram/reportes/${plataforma}/delete/`),

  // ============================================
  // TASKS & TIMELOGS
  // ============================================
  getTasks: (params) => api.get('/tasks/', { params }),
  getTask: (id) => api.get(`/tasks/${id}/`),
  createTask: (data) => api.post('/tasks/', data),
  updateTask: (id, data) => api.patch(`/tasks/${id}/`, data),
  deleteTask: (id) => api.delete(`/tasks/${id}/`),
  startTimer: (taskId) => api.post(`/tasks/${taskId}/start_timer/`),
  stopTimer: (taskId) => api.post(`/tasks/${taskId}/stop_timer/`),
  completeTask: (taskId) => api.post(`/tasks/${taskId}/complete/`),

  getTimeLogs: (params) => api.get('/timelogs/', { params }),
  getTimeLog: (id) => api.get(`/timelogs/${id}/`),

  // ============================================
  // ENGAGEMENT PREDICTION
  // ============================================
  getEngagementPrediction: (postId) => api.get(`/posts/${postId}/engagement-prediction/`),

  // ============================================
  // INFORMES
  // ============================================
  generateInforme: (data) => {
    return api.post('/posts/generar-informe/', data, {
      responseType: 'blob', // Importante para descargar archivos
    });
  },

  // ============================================
  // USO DE PLATAFORMA (user activity)
  // ============================================
  pingActivity: (path) =>
    apiRoot.post('api/user-activity/ping/', { path }),
  getUserActivity: () =>
    apiRoot.get('api/user-activity/'),

  // ============================================
  // APROBACIONES PENDIENTES
  // ============================================
  getAprobacionesPendientes: () =>
    api.get('/aprobaciones-pendientes/'),

  // Posts con validación en <= `dias` días a los que aún les falta enviar idea creativa / copy / contenido
  getPendientesEnvio: (dias = 1) =>
    api.get('/pendientes-envio/', { params: { dias } }),

  markOverdueAsPublished: () =>
    api.post('/posts/mark-overdue-published/'),

  // ============================================
  // PLANTILLAS PPT
  // ============================================
  getPPTTemplates: () =>
    apiRoot.get('instagram/ppt/templates/'),

  /** Paso 1: obtener URL firmada para subir directo a Spaces */
  getPPTPresignedUrl: (filename, clienteSlug) =>
    apiRoot.get('instagram/ppt/presigned-url/', {
      params: { filename, cliente_slug: clienteSlug },
    }),

  /** Paso 2: subir el archivo DIRECTAMENTE a Spaces con la presigned URL */
  uploadFileToSpaces: (presignedUrl, file) =>
    fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'x-amz-acl': 'public-read',
      },
      body: file,
    }),

  /** Paso 3: guardar la URL pública en la BD */
  savePPTTemplate: (nombre, descripcion, clienteSlug, archivoUrl) =>
    apiRoot.post('instagram/ppt/templates/', {
      nombre,
      descripcion,
      cliente_slug: clienteSlug,
      archivo_url:  archivoUrl,
    }),

  deletePPTTemplate: (id) =>
    apiRoot.delete(`instagram/ppt/templates/${id}/delete/`),

  getPPTTokensCatalog: () =>
    apiRoot.get('instagram/ppt/tokens/'),

  generatePPT: (templateId, igUserId, since, until) =>
    apiRoot.post('instagram/ppt/generate/', {
      template_id: templateId,
      ig_user_id:  igUserId,
      since,
      until,
    }, { responseType: 'blob' }),
};

export default contentService;
